import { useState, useEffect, useMemo, useCallback } from "react"
import * as ActionCable from "@rails/actioncable"
import { batch } from "@tanstack/react-pacer"
import { revealRequest, createProcessResult } from "~/react/utils/emailSearchUtils"
import { multi_domain_search_reveal_search_index_path } from "~/lib/routes"
import { EmailVerification } from "~/types/serializers/EmailVerification"
import { ResultType } from "../components/multi_domain_search/result"

interface UseEmailSearchConfig {
  initialEmails?: any[]
  context?: string // For WebSocket channel context (e.g., "lookalikes")
  source: string // For reveal request source (e.g., "discover", "search", "lookalikes")
}

/**
 * Shared hook for email search functionality (multi-domain search and lead lookalikes)
 * Provides reveal functionality, WebSocket updates, and state management
 */
export const useEmailSearch = (config: UseEmailSearchConfig = { source: "discover" }) => {
  const { initialEmails = [], source, context } = config

  // State
  const [results, setResults] = useState(initialEmails)
  const [revealingIds, setRevealingIds] = useState<number[]>([])
  const [revealedIds, setRevealedIds] = useState<number[]>([])
  const [firstRevealAt, setFirstRevealAt] = useState<Date | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  // Create batched result processor
  const processResult = useMemo(
    () => batch(
      createProcessResult(setResults, setRevealedIds, setRevealingIds, setFirstRevealAt),
      {
        maxSize: 10,
        wait: 250,
      }
    ),
    []
  )

  // WebSocket subscription
  useEffect(() => {
    const cable = ActionCable.createConsumer()
    const channelParams: any = { channel: "MultiDomainSearchChannel" }
    if (context) {
      channelParams.context = context
    }

    const subscription = cable.subscriptions.create(channelParams, {
      received: (data: { email: ResultType } | { verification: EmailVerification },) => {
        // Handle different data formats
        if ("email" in data) {
          processResult(data.email)
        } else if ("verification" in data) {
          setResults((prev) =>
            prev.map((result) =>
              result.id === data.verification.email_id
                ? { ...result, verification: data.verification }
                : result,
            ),
          )
        }
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [context, processResult])

  // Handle reveal for a single email
  const handleReveal = useCallback(async (id: number) => {
    const result = results.find((r) => r.id === id)
    if (!result) return

    if (result.type === "generic") {
      const response = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        { generic_email_domains: [result.domain] }
      )

      if (response === "lack_of_credits") {
        setRevealingIds([])
      } else if (!result.revealed) {
        setRevealingIds((prev) => [...prev, id])
      }
    } else {
      const response = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        { email_ids: [id] }
      )

      if (response === "lack_of_credits") {
        setRevealingIds([])
      } else if (!result.revealed) {
        setRevealingIds((prev) => [...prev, id])
      }
    }
  }, [results])

  // Handle reveal for multiple emails
  const handleRevealMultiple = useCallback(async (ids: number[], options?: { leads_list_id?: number }) => {
    const emailsToReveal = results.filter((r) => ids.includes(r.id))

    if (emailsToReveal.length === 0) return

    const genericEmails = emailsToReveal.filter((r) => r.type === "generic")
    const regularEmails = emailsToReveal.filter((r) => r.type !== "generic")

    // Only add non-revealed emails to revealingIds
    const nonRevealedIds = emailsToReveal.filter((r) => !r.revealed).map((r) => r.id)
    setRevealingIds((prev) => [...prev, ...nonRevealedIds])

    // Handle generic emails
    if (genericEmails.length > 0) {
      const genericDomains = [...new Set(genericEmails.map((r) => r.domain))]
      const requestBody: any = {
        generic_email_domains: genericDomains,
      }
      if (options?.leads_list_id !== undefined) {
        requestBody.leads_list_id = options.leads_list_id
      }
      const response = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        requestBody
      )

      if (response === "lack_of_credits") {
        setRevealingIds([])
      }
    }

    // Handle regular emails
    if (regularEmails.length > 0) {
      const requestBody: any = {
        email_ids: regularEmails.map((r) => r.id),
      }
      if (options?.leads_list_id !== undefined) {
        requestBody.leads_list_id = options.leads_list_id
      }
      const response = await revealRequest(
        multi_domain_search_reveal_search_index_path(),
        "POST",
        requestBody
      )

      if (response === "lack_of_credits") {
        setRevealingIds([])
      }
    }
  }, [results])

  // Handle selection
  const handleSelect = useCallback((ids: number[]) => {
    setSelectedIds((prev) => {
      const newIds = [...prev]
      ids.forEach(id => {
        const index = newIds.indexOf(id)
        if (index > -1) {
          newIds.splice(index, 1)
        } else {
          newIds.push(id)
        }
      })
      return newIds
    })
  }, [])

  return {
    // State
    results,
    setResults,
    revealingIds,
    revealedIds,
    firstRevealAt,
    selectedIds,

    // Actions
    handleReveal,
    handleRevealMultiple,
    handleSelect,
    processResult,

    // Utilities
    revealRequest,
  }
}
